import React, { useCallback, useEffect, useState } from 'react';
import { documentsAPI } from '../api/client';
import { extractErrorMessage, quotaFromError } from '../utils/errors';
import QuotaLimitModal from './QuotaLimitModal';

/**
 * "Does this actually read like a Policy?" (GitHub issue #34).
 *
 * Advisory only -- nothing here blocks anything, and this component never
 * implies otherwise. Fetches the most recent evaluation on mount
 * (`GET .../type-evaluation`, which answers `null` when nothing has run
 * yet); `canRun` additionally offers a button to request one
 * (`POST .../type-evaluation`).
 *
 * Every number here is the APPLICATION's arithmetic, not the model's --
 * the per-signal table shows weight × score = contribution explicitly, so
 * the total is auditable to the person reading the panel, not just to
 * whoever reads the database row. See services/ai_evaluation/scoring.py.
 *
 * Model output (notes, evidence quotes, strengths/gaps/recommendations)
 * renders as plain React TEXT NODES ONLY -- no dangerouslySetInnerHTML, no
 * RichContentView, no RedlineView. There is nothing here that could render
 * markup even if the server were somehow made to send some.
 */

const COMPLIANCE_LABELS = {
  compliant: 'Compliant',
  partial: 'Partial Match',
  non_compliant: 'Non-Compliant',
  indeterminate: 'Indeterminate',
};

const COMPLIANCE_COLORS = {
  compliant: { background: 'var(--success-bg)', color: 'var(--success-ink)' },
  partial: { background: 'var(--warning-bg)', color: 'var(--warning-ink)' },
  non_compliant: { background: 'var(--danger-bg)', color: 'var(--danger-ink)' },
  indeterminate: { background: 'var(--neutral-bg)', color: 'var(--neutral-ink)' },
};

function FindingList({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>{title}</div>
      <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
        {items.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index} style={{ fontSize: '13.5px' }}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function TypeEvaluationPanel({ documentId, revisionId = null, canRun = false }) {
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [quotaError, setQuotaError] = useState(null); // issue #159

  const fetchLatest = useCallback(async () => {
    try {
      const res = await documentsAPI.getTypeEvaluation(documentId, revisionId);
      setEvaluation(res.data);
    } catch (err) {
      // Non-fatal and deliberately silent: not being able to load this must
      // never stop the page around it from rendering.
      console.error('Error loading type evaluation:', err);
      setEvaluation(null);
    } finally {
      setLoading(false);
    }
  }, [documentId, revisionId]);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await documentsAPI.runTypeEvaluation(documentId, { revisionId });
      setEvaluation(res.data);
    } catch (err) {
      console.error('Error running type evaluation:', err);
      const quota = quotaFromError(err);
      if (quota) setQuotaError(quota);
      else setError(extractErrorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-header"><h3>🏷️ Document Type Check</h3></div>
        <div className="loading">Loading…</div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3>🏷️ Document Type Check</h3>
        {canRun && (
          <button className="btn btn-secondary btn-small" onClick={handleRun} disabled={running}>
            {running ? '⏳ Running…' : evaluation ? '🔄 Re-run' : '▶️ Run evaluation'}
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      <QuotaLimitModal quota={quotaError} onClose={() => setQuotaError(null)} />

      {!evaluation ? (
        <div style={{ color: 'var(--muted)', fontSize: '13.5px' }}>
          {canRun
            ? "Not evaluated yet -- check whether this document's content matches the "
              + 'requirements for its declared type.'
            : 'This document has not been evaluated against its declared type.'}
        </div>
      ) : evaluation.status === 'failed' ? (
        <div className="alert alert-error" role="status">
          Evaluation failed: {evaluation.error_detail || 'unknown error'}.
          {canRun && ' Try running it again.'}
        </div>
      ) : (
        <>
          {evaluation.is_stub && (
            <div className="alert alert-warning" role="status" style={{ marginBottom: '12px' }}>
              Generated without a language model -- not a real evaluation.
            </div>
          )}
          {evaluation.truncated && (
            <div className="alert alert-info" role="status" style={{ marginBottom: '12px' }}>
              This document was too long for a full read; the evaluation below is based on a
              truncated version (every section heading was still considered).
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '20px', marginBottom: '14px' }}>
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Expected type</div>
              <div>{evaluation.expected_type || '—'}</div>
            </div>
            {evaluation.determined_type && (
              <div>
                <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Reads like</div>
                <div>{evaluation.determined_type}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Match score</div>
              <div>
                {evaluation.match_score != null ? `${Math.round(evaluation.match_score * 100)}%` : '—'}
              </div>
            </div>
            {evaluation.compliance_status && (
              <span className="badge" style={COMPLIANCE_COLORS[evaluation.compliance_status] || {}}>
                {COMPLIANCE_LABELS[evaluation.compliance_status] || evaluation.compliance_status}
              </span>
            )}
          </div>

          {evaluation.signals.length > 0 && (
            <div className="table-container" style={{ marginBottom: '14px' }}>
              <table>
                <thead>
                  <tr>
                    <th>Signal</th>
                    <th>Score</th>
                    <th>Weight</th>
                    <th>Contribution</th>
                    <th>Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {evaluation.signals.map((signal) => (
                    <tr key={signal.key}>
                      <td style={{ textTransform: 'capitalize' }}>{signal.key}</td>
                      <td>{signal.score != null ? signal.score.toFixed(2) : '—'}</td>
                      <td>{signal.weight != null ? signal.weight.toFixed(2) : '—'}</td>
                      <td>{signal.contribution != null ? signal.contribution.toFixed(3) : '—'}</td>
                      <td style={{ fontSize: '12.5px', color: 'var(--muted)' }}>
                        {signal.note}
                        {signal.evidence_quote && (
                          <div style={{ fontStyle: 'italic', marginTop: '2px' }}>
                            "{signal.evidence_quote}"
                            {!signal.evidence_present && (
                              <span style={{ color: 'var(--warning-ink)' }}> (context no longer present)</span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <FindingList title="Missing sections" items={evaluation.missing_sections} />
          <FindingList title="Prohibited signals found" items={evaluation.prohibited_signals_found} />
          <FindingList title="Strengths" items={evaluation.strengths} />
          <FindingList title="Gaps" items={evaluation.gaps} />
          <FindingList title="Recommendations" items={evaluation.recommendations} />
          <FindingList title="Caveats" items={evaluation.caveats} />

          <div style={{ fontSize: '11.5px', color: 'var(--muted)', marginTop: '10px' }}>
            Scored under rubric v{evaluation.rubric_version} · {evaluation.scorer_version}
            {' · '}{new Date(evaluation.created_at).toLocaleString()}
          </div>
        </>
      )}
    </div>
  );
}

export default TypeEvaluationPanel;
